import * as api from "@/api";
import ImportantTip from "@/components/important-tip";
import LoadingScreen from "@/components/loading-screen";
import ProfileCard from "@/components/profile-card";
import * as signals from "@/signals";
import { ProfileCardItem, ProfileResponse } from "@/types";
import {
	changeEmail,
	changePhone,
	copyText,
	logOut,
	showDialog,
	showPrompt,
} from "@/utils";
import {
	faCakeCandles,
	faCalendarDay,
	faEarth,
	faEnvelope,
	faKey,
	faMobileScreen,
	faSignature,
	faUser,
} from "@fortawesome/free-solid-svg-icons";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { t } from "i18next";
import { JSX } from "react";
import { useNavigate } from "react-router-dom";

interface JsxItem {
	jsx: JSX.Element;
}

function ProfilePage(): JSX.Element {
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const { data, error, isFetching } = useQuery({
		queryFn: () => {
			if (!signals.login.value.username) {
				return null;
			}
			return api.getProfile(["regTime"], () => {
				void logOut();
			});
		},
		queryKey: ["profile", signals.login.value.username],
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		retry: false,
	});

	const changeBirthday = async (): Promise<void> => {
		const newBirthday = await showPrompt(t("enterNewBirthday"), {
			defaultText:
				signals.login.value.birthday ||
				new Date().toISOString().split("T")[0],
		});
		if (!signals.login.value.username || !newBirthday) {
			return;
		}
		const newBirthdayDate = new Date(newBirthday);
		const formattedNewBirthday =
			newBirthdayDate.toString() !== "Invalid Date"
				? newBirthdayDate.toISOString().split("T")[0]
				: "";
		const year = newBirthdayDate.getFullYear();
		if (
			!formattedNewBirthday ||
			year < 1900 ||
			year > new Date().getFullYear()
		) {
			void showDialog(t("invalidDate"));
			return;
		}
		if (formattedNewBirthday !== signals.login.value.birthday) {
			signals.isLoadingScreenShown.value = true;
			try {
				await api.updateBirthday(formattedNewBirthday);
				signals.login.value = {
					...signals.login.value,
					birthday: formattedNewBirthday,
				};
			} catch (error) {
				api.handleApiError(error);
			} finally {
				signals.isLoadingScreenShown.value = false;
			}
		}
	};

	const changeName = async (): Promise<void> => {
		const newName = await showPrompt(t("enterNewName"), {
			defaultText: signals.login.value.name,
		});
		if (
			!signals.login.value.username ||
			!newName ||
			newName === signals.login.value.name
		) {
			return;
		}
		signals.isLoadingScreenShown.value = true;
		try {
			const data = await api.updateName(newName);
			if (data.alert) {
				void showDialog(data.alert);
			}
			if (!data.success) {
				return;
			}
			signals.login.value = {
				...signals.login.value,
				name: newName,
			};
		} catch (error) {
			api.handleApiError(error);
		} finally {
			signals.isLoadingScreenShown.value = false;
		}
	};

	const copyUserId = (): void => {
		if (!signals.login.value.username) {
			return;
		}
		void copyText(signals.login.value.username);
	};

	const updateRegion = async (): Promise<void> => {
		// overseas account transfer to China
		if (
			signals.dynamicInfo.value.region === "CN" &&
			!signals.login.value.phone
		) {
			return changePhone();
		}

		// China account transfer to overseas
		const isChinaTimeZone =
			Intl.DateTimeFormat().resolvedOptions().timeZone ===
			"Asia/Shanghai";
		if (data?.region === "CN" && isChinaTimeZone) {
			return void showDialog(
				t("regionUpdatedAs", {
					region: "CN",
				}),
			);
		}

		signals.isLoadingScreenShown.value = true;
		try {
			const data = await api.updateRegion();
			if (data.error) {
				return showDialog(data.error);
			}
			if (!data.success) {
				return;
			}
			queryClient.setQueryData<ProfileResponse>(
				["profile", signals.login.value.username],
				(profile) => {
					if (!profile || !data.newValue) {
						return;
					}
					return {
						...profile,
						region: data.newValue,
					};
				},
			);
			void showDialog(
				t("regionUpdatedAs", {
					region: data.newValue,
				}),
			);
		} catch (error) {
			api.handleApiError(error);
		} finally {
			signals.isLoadingScreenShown.value = false;
		}
	};

	const cards: (ProfileCardItem | JsxItem)[] = [
		{
			action: {
				onClick: copyUserId,
				text: t("copy"),
				title: t("copyUserId"),
			},
			content: signals.login.value.username,
			icon: faUser,
			label: t("userId"),
		},
		{
			action: {
				onClick: (): void => {
					void changeEmail(signals.login.value.email);
				},
				text: t("change"),
				title: t("changeEmail"),
			},
			content: signals.login.value.email,
			icon: faEnvelope,
			label: t("email"),
		},
		{
			action: {
				onClick: (): void => {
					void changePhone();
				},
				text: t("change"),
				title: t("changePhoneNumber"),
			},
			content: signals.login.value.phone,
			icon: faMobileScreen,
			label: t("phoneNumber"),
		},
		{
			action: {
				onClick: (): void => {
					void navigate("/.well-known/change-password", {
						state: {
							emailOrPhone:
								signals.login.value.email ||
								signals.login.value.phone,
						},
					});
				},
				text: t("change"),
				title: t("changePassword"),
			},
			content: signals.login.value.token ? "********" : "",
			icon: faKey,
			label: t("password"),
		},
		{
			action: {
				onClick: changeName,
				text: t("change"),
				title: t("changeName"),
			},
			content: signals.login.value.name,
			icon: faSignature,
			label: t("name"),
		},
		{
			action: {
				onClick: changeBirthday,
				text: t("change"),
				title: t("changeBirthday"),
			},
			content: signals.login.value.birthday,
			icon: faCakeCandles,
			label: t("birthday"),
		},
		{
			action: {
				onClick: updateRegion,
				text: t("update"),
				title: t("updateRegion"),
			},
			content: data?.region,
			icon: faEarth,
			label: t("region"),
		},
		{
			action: {
				onClick: (): void => {
					const details = data?.regTime?.details;
					if (!details) {
						return;
					}
					void showDialog(details);
				},
				text: t("details"),
				title: t("regTimeDetails"),
			},
			content: data?.regTime?.time,
			icon: faCalendarDay,
			label: t("regTime"),
		},
	];
	const cardElements = cards.map((item) => {
		if (item.jsx) {
			return item.jsx;
		}
		const ProfileCardItem = item as ProfileCardItem;
		return (
			<ProfileCard
				item={ProfileCardItem}
				key={ProfileCardItem.label}
			/>
		);
	});

	return (
		<main className="profile">
			<h2>{t("profile")}</h2>
			<div className="profile-cards-container">{cardElements}</div>
			{error && (
				<ImportantTip
					text={error.message}
					title={t("error")}
				/>
			)}
			{isFetching && <LoadingScreen />}
		</main>
	);
}

export default ProfilePage;
